import React from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import type { IconType } from 'react-icons';
import {
  RiErrorWarningLine,
  RiLock2Line,
  RiSearchLine,
  RiShieldKeyholeLine,
} from 'react-icons/ri';

export type ErrorStatus = 401 | 403 | 404 | 500 | 'error';

export type ErrorLayoutProps = {
  eyebrow: string;
  title: string;
  description?: ReactNode;
  status?: ErrorStatus;
  accentColor?: string;
  icon?: IconType;
  logo?: ReactNode;
  centerContent?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
} & Omit<HTMLAttributes<HTMLDivElement>, 'title'>;

const DEFAULT_ACCENTS: Record<ErrorStatus, { color: string; icon: IconType }> =
  {
    401: { color: '#D97706', icon: RiLock2Line },
    403: { color: '#D97706', icon: RiShieldKeyholeLine },
    404: { color: '#9CA3AF', icon: RiSearchLine },
    500: { color: '#DC2626', icon: RiErrorWarningLine },
    error: { color: '#DC2626', icon: RiErrorWarningLine },
  };

const SQUARES = [
  { x: 397.504, y: 163.907, size: 95 },
  { x: 340.254, y: 127.434, size: 191 },
  { x: 283.002, y: 90.961, size: 287 },
  { x: 225.75, y: 54.4874, size: 383 },
  { x: 168.5, y: 18.0147, size: 479 },
  { x: 111.248, y: -18.4587, size: 575 },
  { x: 53.9958, y: -54.9316, size: 671 },
  { x: -3.25422, y: -91.4052, size: 767 },
];

export const RotatedSquaresBg: React.FC = () => (
  <div
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
  >
    <div className="absolute top-10 left-1/2 w-[500px] -translate-x-1/2 opacity-50 sm:-top-10 sm:w-[700px] sm:opacity-70 md:-top-[60px] md:w-[820px] md:opacity-100 lg:w-[900px]">
      <svg
        width="909"
        height="655"
        viewBox="0 0 909 655"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ width: '100%', height: 'auto' }}
      >
        {SQUARES.map(({ x, y, size }) => (
          <rect
            key={size}
            x={x}
            y={y}
            width={size}
            height={size}
            rx="19.5"
            transform={`rotate(-12.5 ${x} ${y})`}
            stroke="#E9EAEB"
          />
        ))}
      </svg>
    </div>
  </div>
);

const ErrorLayout: React.FC<ErrorLayoutProps> = ({
  eyebrow,
  title,
  description,
  status = 'error',
  accentColor,
  icon,
  logo,
  centerContent,
  actions,
  children,
  className,
  ...rest
}) => {
  const preset = DEFAULT_ACCENTS[status] ?? DEFAULT_ACCENTS.error;
  const accent = accentColor ?? preset.color;
  const BadgeIcon = icon ?? preset.icon;

  return (
    <div
      className={`relative flex min-h-screen w-full items-center overflow-hidden bg-white ${className ?? ''}`.trim()}
      {...rest}
    >
      <RotatedSquaresBg />
      <div className="relative z-10 mx-auto flex w-full max-w-[1152px] flex-col items-center justify-between gap-12 px-6 py-16 sm:px-12 md:flex-row">
        <div className="flex flex-1 flex-col items-start gap-6">
          {logo && (
            <div className="relative h-[52px] w-[220px] sm:h-16 sm:w-[280px]">
              {logo}
            </div>
          )}
          <span
            className="text-lg font-bold tracking-wide"
            style={{ color: accent }}
          >
            {eyebrow}
          </span>
          <h1 className="text-[3rem] leading-[1.1] font-extrabold text-[#1B1B1B] sm:text-[3.75rem]">
            {title}
          </h1>
          {typeof description === 'string' ? (
            <p className="max-w-md text-lg leading-[1.6] text-[#5A5E67] sm:text-xl">
              {description}
            </p>
          ) : (
            description
          )}
          {actions && (
            <div className="mt-2 flex flex-wrap items-center gap-4">
              {actions}
            </div>
          )}
          {children}
        </div>

        <div className="flex flex-1 items-center justify-center">
          <div className="relative h-[300px] w-[300px] sm:h-[400px] sm:w-[400px]">
            <div className="absolute inset-0 rounded-full border border-[#E9EAEB] bg-[linear-gradient(to_bottom_right,#F8F9FA,#fff)] shadow-lg" />
            <div className="absolute inset-10 rounded-full border border-[#E9EAEB] bg-[linear-gradient(to_bottom_right,#fff,#F1F3F5)] shadow-md" />
            <div className="absolute inset-20 flex items-center justify-center rounded-full border border-[#E9EAEB] bg-white shadow-sm">
              {centerContent ?? <BadgeIcon size={72} color={accent} />}
            </div>
            <div
              className="absolute right-[55px] bottom-[55px] flex h-20 w-20 items-center justify-center rounded-full shadow-md"
              style={{ backgroundColor: accent }}
            >
              <BadgeIcon size={34} color="#fff" />
            </div>
            <div className="absolute top-7 right-[18px] h-5 w-5 rounded-full bg-[#E9EAEB]" />
            <div className="absolute top-[58px] -right-0.5 h-3 w-3 rounded-full bg-[#E9EAEB]" />
            <div className="absolute bottom-[38px] left-2 h-4 w-4 rounded-full bg-[#E9EAEB]" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorLayout;
